class PdpColorVideo extends HTMLElement {
  connectedCallback() {
    this.playButton = this.querySelector(".pdp-color-video__play");
    this.carousel = this.closest(".product-gallery__carousel");
    this.autoplayMode = this.getAttribute("data-playback") === "delayed-autoplay";
    this.manualPause = false;
    this.suspended = false;
    this.fallbackToClick = false;
    this.mediaFailed = false;
    this.pageLoaded = false;
    this.idleReached = false;
    this.inView = false;
    this.onPlayClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (this.autoplayMode && !this.fallbackToClick) {
        this.onAutoplayControl();
        return;
      }
      this.play();
    };
    this.onCarouselChange = (event) => {
      const leftSlide = this.hasLeftSlide(event);
      if (!this.autoplayMode || this.fallbackToClick) {
        if (!this.player || !leftSlide) {
          return;
        }
        this.player.pause();
        return;
      }
      if (leftSlide) {
        this.suspend();
        return;
      }
      if (this.player) {
        this.resumeIfSuspended();
        return;
      }
      this.maybeStart();
    };
    this.onVisibilityChange = () => {
      if (!this.autoplayMode || this.fallbackToClick) {
        if (document.hidden && this.player) {
          this.player.pause();
        }
        return;
      }
      if (document.hidden) {
        this.suspend();
        return;
      }
      this.resumeIfSuspended();
      this.maybeStart();
    };
    this.playButton?.addEventListener("click", this.onPlayClick);
    this.carousel?.addEventListener("carousel:change", this.onCarouselChange);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    if (this.autoplayMode) {
      this.classList.add("is-autoplay");
      this.armGuardedAutoplay();
    }
  }

  disconnectedCallback() {
    this.playButton?.removeEventListener("click", this.onPlayClick);
    this.carousel?.removeEventListener("carousel:change", this.onCarouselChange);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("load", this.onWindowLoad);
    this.motionQuery?.removeEventListener("change", this.onMotionChange);
    this.connection?.removeEventListener("change", this.onConnectionChange);
    this.observer?.disconnect();
    if (this.idleHandle && window.cancelIdleCallback) {
      window.cancelIdleCallback(this.idleHandle);
    }
    clearTimeout(this.idleTimer);
    this.releasePlayer();
  }

  armGuardedAutoplay() {
    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.onMotionChange = () => {
      if (this.motionQuery.matches) {
        this.suspend();
      } else {
        this.resumeIfSuspended();
        this.maybeStart();
      }
    };
    this.motionQuery.addEventListener("change", this.onMotionChange);
    this.connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    this.onConnectionChange = () => {
      this.resumeIfSuspended();
      this.maybeStart();
    };
    this.connection?.addEventListener("change", this.onConnectionChange);
    this.observer = new IntersectionObserver((entries) => {
      this.inView = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
      if (!this.autoplayMode || this.fallbackToClick) {
        return;
      }
      if (this.inView) {
        if (this.player) {
          this.resumeIfSuspended();
        } else {
          this.maybeStart();
        }
        return;
      }
      this.suspend();
    }, { threshold: [0, 0.5] });
    this.observer.observe(this);
    this.onWindowLoad = () => {
      this.pageLoaded = true;
      const markIdle = () => {
        this.idleReached = true;
        this.maybeStart();
      };
      if (window.requestIdleCallback) {
        this.idleHandle = window.requestIdleCallback(markIdle, { timeout: 2000 });
      } else {
        this.idleTimer = window.setTimeout(markIdle, 400);
      }
    };
    if (document.readyState === "complete") {
      this.onWindowLoad();
    } else {
      window.addEventListener("load", this.onWindowLoad, { once: true });
    }
  }

  maybeStart() {
    if (!this.autoplayMode || this.fallbackToClick || this.mediaFailed || this.manualPause || this.player) {
      return;
    }
    if (!this.canAutoplay()) {
      return;
    }
    this.beginAutoplay();
  }

  canAutoplay() {
    if (!this.pageLoaded || !this.idleReached || !this.inView || document.hidden) {
      return false;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return false;
    }
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      if (connection.saveData) {
        return false;
      }
      if (connection.effectiveType === "slow-2g" || connection.effectiveType === "2g") {
        return false;
      }
    }
    return this.isVideoSlideActive();
  }

  isVideoSlideActive() {
    const carousel = this.carousel;
    if (!carousel) {
      return true;
    }
    const snap = getComputedStyle(carousel).scrollSnapType || "";
    if (!snap || snap === "none") {
      return true;
    }
    const slide = this.closest(".product-gallery__media");
    if (!slide || slide.hasAttribute("hidden")) {
      return false;
    }
    const cells = carousel.cells || [];
    if (!cells.length) {
      return true;
    }
    const scroll = Math.abs(carousel.scrollLeft);
    let active = null;
    let best = Infinity;
    cells.forEach((cell) => {
      if (cell.hasAttribute("hidden")) {
        return;
      }
      const distance = Math.abs(Math.abs(cell.offsetLeft) - scroll);
      if (distance < best) {
        best = distance;
        active = cell;
      }
    });
    return Boolean(active && (active === slide || active.contains(this)));
  }

  hasLeftSlide(event) {
    const slide = this.closest(".product-gallery__media");
    if (slide && slide.hasAttribute("hidden")) {
      return true;
    }
    const snap = this.carousel ? getComputedStyle(this.carousel).scrollSnapType : "";
    const cell = event && event.detail && event.detail.cell;
    return Boolean(snap && snap !== "none" && cell && !cell.contains(this));
  }

  onAutoplayControl() {
    if (this.player && !this.player.paused) {
      this.manualPause = true;
      this.suspended = false;
      this.player.pause();
      this.classList.remove("is-playing");
      this.setControlState("play");
      return;
    }
    if (!this.canAutoplay()) {
      this.useClickToPlay();
      return;
    }
    this.manualPause = false;
    this.suspended = false;
    this.beginAutoplay();
  }

  beginAutoplay() {
    this.mediaFailed = false;
    this.querySelector(".pdp-color-video__error")?.setAttribute("hidden", "");
    this.classList.remove("is-error");
    this.classList.add("is-autoplay");
    this.scheduleSpinner();
    if (this.player) {
      const player = this.player;
      player.play().then(() => this.continueAutoplay(player)).catch(() => this.onAutoplayRejected());
      return;
    }

    const source = this.selectSource();
    if (!source) {
      this.showError();
      return;
    }

    const player = document.createElement("video");
    player.className = "pdp-color-video__player";
    player.controls = false;
    player.muted = true;
    player.defaultMuted = true;
    player.loop = true;
    player.playsInline = true;
    player.preload = "none";
    player.setAttribute("muted", "");
    player.setAttribute("playsinline", "");
    const label = this.closest(".pdp-color-video-slide")?.getAttribute("aria-label");
    if (label) {
      player.setAttribute("aria-label", label);
    }
    const media = document.createElement("source");
    media.src = source.url;
    if (source.mimeType) {
      media.type = source.mimeType;
    }
    player.append(media);
    const failed = () => {
      if (this.player === player) {
        this.showError();
      }
    };
    player.addEventListener("error", failed);
    media.addEventListener("error", failed);
    this.player = player;
    this.append(player);
    player.play().then(() => this.continueAutoplay(player)).catch(() => this.onAutoplayRejected());
  }

  continueAutoplay(player) {
    if (this.player !== player) {
      return;
    }
    if (this.classList.contains("has-frame")) {
      this.markAutoplayPlaying();
      return;
    }
    this.revealWhenFrameReady(player, () => this.markAutoplayPlaying());
  }

  scheduleSpinner() {
    this.cancelSpinner();
    if (this.classList.contains("has-frame")) {
      return;
    }
    this.spinnerTimer = window.setTimeout(() => {
      this.spinnerTimer = null;
      if (!this.isConnected || !this.player || this.classList.contains("has-frame")) {
        return;
      }
      this.classList.add("is-loading");
      this.playButton?.setAttribute("aria-busy", "true");
    }, 250);
  }

  cancelSpinner() {
    clearTimeout(this.spinnerTimer);
    this.spinnerTimer = null;
    this.classList.remove("is-loading");
    this.playButton?.removeAttribute("aria-busy");
  }

  revealWhenFrameReady(player, onReady) {
    this.cancelFrameWait();
    const ready = () => {
      if (this.player !== player) {
        return;
      }
      this.cancelFrameWait();
      onReady();
    };
    if (typeof player.requestVideoFrameCallback === "function") {
      this.frameCallbackId = player.requestVideoFrameCallback(() => ready());
      return;
    }
    const paint = () => {
      this.frameFallbackId = window.requestAnimationFrame(() => ready());
    };
    if (player.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      paint();
      return;
    }
    this.loadedDataHandler = () => {
      player.removeEventListener("loadeddata", this.loadedDataHandler);
      this.loadedDataHandler = null;
      if (this.player === player) {
        paint();
      }
    };
    player.addEventListener("loadeddata", this.loadedDataHandler);
  }

  cancelFrameWait() {
    if (this.frameCallbackId != null && this.player && typeof this.player.cancelVideoFrameCallback === "function") {
      this.player.cancelVideoFrameCallback(this.frameCallbackId);
    }
    if (this.frameFallbackId != null) {
      window.cancelAnimationFrame(this.frameFallbackId);
    }
    if (this.loadedDataHandler && this.player) {
      this.player.removeEventListener("loadeddata", this.loadedDataHandler);
    }
    this.frameCallbackId = null;
    this.frameFallbackId = null;
    this.loadedDataHandler = null;
  }

  cancelPendingPresentation() {
    this.cancelSpinner();
    this.cancelFrameWait();
  }

  markAutoplayPlaying() {
    if (!this.player) {
      return;
    }
    this.cancelSpinner();
    this.classList.remove("is-error");
    this.classList.add("is-playing", "is-autoplay", "has-frame");
    this.suspended = false;
    this.setControlState("pause");
  }

  onAutoplayRejected() {
    if (!this.isConnected || this.classList.contains("is-error")) {
      return;
    }
    this.releasePlayer();
    this.fallbackToClick = true;
    this.classList.remove("is-autoplay", "is-loading", "is-playing", "has-frame");
    this.playButton?.removeAttribute("aria-busy");
    this.setControlState("play");
  }

  useClickToPlay() {
    this.fallbackToClick = true;
    this.classList.remove("is-autoplay");
    if (this.player && !this.player.controls) {
      this.releasePlayer();
    }
    this.play();
  }

  suspend() {
    if (this.manualPause || !this.player || this.player.paused) {
      return;
    }
    this.suspended = true;
    this.cancelPendingPresentation();
    this.player.pause();
    this.classList.remove("is-playing");
    this.setControlState("play");
  }

  resumeIfSuspended() {
    if (!this.suspended || this.manualPause || this.fallbackToClick || !this.player) {
      return;
    }
    if (!this.canAutoplay()) {
      return;
    }
    const player = this.player;
    if (!this.classList.contains("has-frame")) {
      this.scheduleSpinner();
    }
    player.play().then(() => this.continueAutoplay(player)).catch(() => this.onAutoplayRejected());
  }

  setControlState(state) {
    const button = this.playButton;
    if (!button) {
      return;
    }
    const playLabel = button.getAttribute("data-label-play") || button.getAttribute("aria-label");
    const pauseLabel = button.getAttribute("data-label-pause") || playLabel;
    if (state === "pause") {
      button.dataset.state = "pause";
      button.setAttribute("aria-label", pauseLabel);
    } else {
      delete button.dataset.state;
      button.setAttribute("aria-label", playLabel);
    }
  }

  play() {
    this.mediaFailed = false;
    this.querySelector(".pdp-color-video__error")?.setAttribute("hidden", "");
    this.classList.remove("is-error");
    this.scheduleSpinner();
    if (this.player) {
      const player = this.player;
      player.play().then(() => {
        this.revealWhenFrameReady(player, () => this.markPlaying());
      }).catch(() => this.showError());
      return;
    }

    const source = this.selectSource();
    if (!source) {
      this.showError();
      return;
    }

    const player = document.createElement("video");
    player.className = "pdp-color-video__player";
    player.controls = true;
    player.playsInline = true;
    player.preload = "none";
    player.setAttribute("playsinline", "");
    const label = this.closest(".pdp-color-video-slide")?.getAttribute("aria-label");
    if (label) {
      player.setAttribute("aria-label", label);
    }
    const media = document.createElement("source");
    media.src = source.url;
    if (source.mimeType) {
      media.type = source.mimeType;
    }
    player.append(media);
    const failed = () => {
      if (this.player === player) {
        this.showError();
      }
    };
    player.addEventListener("error", failed);
    media.addEventListener("error", failed);
    this.player = player;
    this.append(player);
    player.play().then(() => {
      this.revealWhenFrameReady(player, () => this.markPlaying());
    }).catch(failed);
  }

  markPlaying() {
    if (!this.player) {
      return;
    }
    this.cancelSpinner();
    this.classList.remove("is-error");
    this.classList.add("is-playing");
    if (document.activeElement === this.playButton) {
      this.player.focus({ preventScroll: true });
    }
  }

  selectSource() {
    const sources = this.readSources().filter((source) => source && source.url);
    const hls = sources.find((source) => this.isHls(source));
    if (hls && this.canPlayNativeHls()) {
      return hls;
    }
    const mp4 = sources.filter((source) => {
      const mime = String(source.mimeType || "");
      const format = String(source.format || "");
      return mime.includes("mp4") || format === "mp4";
    });
    const pool = (mp4.length ? mp4 : sources.filter((source) => {
      const mime = String(source.mimeType || "");
      const format = String(source.format || "");
      return !mime.includes("mpegURL") && format !== "m3u8" && format !== "hls";
    }));
    const ranked = (pool.length ? pool : sources).slice().sort((a, b) => (a.width || 0) - (b.width || 0));
    if (!ranked.length) {
      return null;
    }
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const targetWidth = Math.min(Math.round(this.clientWidth * pixelRatio) || 800, 1400);
    return ranked.find((source) => (source.width || 0) >= targetWidth) || ranked[ranked.length - 1];
  }

  isHls(source) {
    const mime = String(source.mimeType || "");
    const format = String(source.format || "");
    return mime.includes("mpegURL") || format === "m3u8" || format === "hls";
  }

  canPlayNativeHls() {
    const probe = document.createElement("video");
    const support = probe.canPlayType("application/vnd.apple.mpegurl") || probe.canPlayType("application/x-mpegURL");
    return support === "probably" || support === "maybe";
  }

  readSources() {
    const node = this.querySelector('script[type="application/json"]');
    if (!node) {
      return [];
    }
    try {
      const parsed = JSON.parse(node.textContent || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  showError() {
    this.mediaFailed = true;
    this.releasePlayer();
    this.classList.remove("is-playing", "is-loading", "has-frame");
    this.playButton?.removeAttribute("aria-busy");
    this.classList.add("is-error");
    this.setControlState("play");
    this.querySelector(".pdp-color-video__error")?.removeAttribute("hidden");
    this.playButton?.focus({ preventScroll: true });
  }

  releasePlayer() {
    this.cancelPendingPresentation();
    const player = this.player;
    if (!player) {
      return;
    }
    this.player = null;
    player.pause();
    player.removeAttribute("src");
    player.querySelectorAll("source").forEach((source) => source.remove());
    player.load();
    player.remove();
  }
}

if (!window.customElements.get("pdp-color-video")) {
  window.customElements.define("pdp-color-video", PdpColorVideo);
}
